import React, { useEffect, useMemo, useState } from "react";
import { hqApi } from "@/hq/hqApi";
import { Card, Skeleton } from "@/hq/HQPrimitives";
import { toast } from "sonner";
import {
  X, Send, Save, FlaskConical, Code2, Monitor, Smartphone,
  AlertTriangle, Copy, CheckCircle2,
} from "lucide-react";
import { asText } from "@/hq/asText";

const APPROVED_PLACEHOLDERS = [
  { key: "CONTACT_NAME",    hint: "First name of the contact" },
  { key: "BUSINESS_NAME",   hint: "Prospect business name" },
  { key: "INTEREST_CTA_URL", hint: "Unique tracked \"I'M INTERESTED\" link" },
  { key: "SENDER_NAME",     hint: "Name of the HQ operator sending this" },
  { key: "ORANGE_LOGO",     hint: "OrangeBySite logo URL" },
];

const fmt = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-3 py-1.5 rounded-full text-[11px] tracking-[0.16em] uppercase font-black
        border transition-colors ${active
          ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
          : "bg-white text-[#4A4A4A] border-[#EAEAEA] hover:border-[#0A0A0A]"}`}
    >{children}</button>
  );
}

function PlaceholderChip({ token, hint, onInsert }) {
  const [copied, setCopied] = useState(false);
  const tag = `{{${token}}}`;
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(tag); } catch (_) {}
    setCopied(true); setTimeout(() => setCopied(false), 1400);
  };
  return (
    <div className="flex items-center gap-2 py-1 border-b border-dashed border-[#EAEAEA]">
      <div className="flex-1 min-w-0">
        <div className="font-mono text-[11px] text-[#0A0A0A]">{tag}</div>
        <div className="text-[10px] text-[#8A8A8A]">{hint}</div>
      </div>
      <button
        type="button"
        onClick={() => onInsert?.(tag)}
        data-testid={`followup-insert-${token}`}
        className="text-[10px] tracking-[0.16em] uppercase font-black px-2 py-1 rounded-full border border-[#EAEAEA] text-[#4A4A4A] hover:border-[#0A0A0A]"
      >Insert</button>
      <button
        type="button"
        onClick={doCopy}
        aria-label={`Copy ${tag}`}
        className={`inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] tracking-[0.14em] uppercase font-black border ${copied ? "text-[#0F7B3E] border-[#B7EBCF] bg-[#EBFBF2]" : "text-[#8A8A8A] border-[#EAEAEA] hover:text-[#0A0A0A] hover:border-[#0A0A0A]"} transition-colors`}
      >{copied ? <><CheckCircle2 size={10}/> Copied</> : <><Copy size={10}/> Copy</>}</button>
    </div>
  );
}

/**
 * Phase 3I.4S — Follow-Up Generator modal.
 *
 * Manual HTML follow-up creation → sanitise → preview → send-test → send.
 * `parent` is the pitch detail row currently open in PitchDetailModal.
 */
export default function FollowUpGeneratorModal({ parent, onClose, onSent }) {
  const [tab, setTab] = useState("code"); // "code" | "desktop" | "mobile"
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [rendered, setRendered] = useState(null); // {html, warnings, unknown_placeholders, blocked}
  const [renderError, setRenderError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [testRecipient, setTestRecipient] = useState("");
  const [showTest, setShowTest] = useState(false);

  const commId = parent?.id;

  // Prefill test recipient with the logged-in operator's email from a
  // lightweight /whoami — falls back to blank if unavailable.
  useEffect(() => {
    hqApi.get("/auth/me").then((r) => {
      if (r.data?.email) setTestRecipient(r.data.email);
    }).catch(() => {});
  }, []);

  const insertPlaceholder = (tag) => {
    setHtml((prev) => (prev || "") + tag);
    setTab("code");
  };

  const renderNow = async ({ autoSwitch = true } = {}) => {
    if (!html.trim()) {
      setRenderError("Paste HTML first, then click Render.");
      return null;
    }
    setBusy(true);
    setRenderError(null);
    try {
      const r = await hqApi.post(`/control/pitches/${commId}/followups/render`,
                                  { html, subject });
      setRendered(r.data);
      if (r.data.blocked) {
        toast.warning("Some HTML was stripped for safety — see warnings.");
      }
      // Auto-switch: PASTE → RENDER → DESKTOP so the operator sees the
      // rendered email immediately without a second click.
      if (autoSwitch) setTab("desktop");
      return r.data;
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : (d?.message || e.message || "Unable to render email.");
      setRenderError(msg);
      toast.error(msg);
      return null;
    } finally { setBusy(false); }
  };

  const saveDraft = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Subject and HTML required"); return; }
    setBusy(true);
    try {
      const body = { subject, html };
      const r = draftId
        ? await hqApi.patch(`/control/pitches/${commId}/followups/${draftId}`, body)
        : await hqApi.post(`/control/pitches/${commId}/followups`, body);
      setDraftId(r.data.id);
      toast.success("Draft saved");
      onSent?.(); // refresh follow-up list
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save draft");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!subject.trim() || !html.trim()) { toast.error("Subject and HTML required"); return; }
    if (!testRecipient.trim() || !testRecipient.includes("@")) {
      toast.error("A valid test recipient email is required"); return;
    }
    setBusy(true);
    try {
      // Save draft first so backend has html to send
      let id = draftId;
      if (!id) {
        const r = await hqApi.post(`/control/pitches/${commId}/followups`, { subject, html });
        id = r.data.id; setDraftId(id);
      } else {
        await hqApi.patch(`/control/pitches/${commId}/followups/${id}`, { subject, html });
      }
      const r = await hqApi.post(
        `/control/pitches/${commId}/followups/${id}/send-test`,
        { test_recipient: testRecipient },
      );
      if (r.data.status === "sent") {
        toast.success(`Test sent to ${testRecipient}`);
      } else if (r.data.status === "skipped") {
        toast.info("Test send skipped (email provider not configured)");
      } else {
        toast.error("Test send failed");
      }
      setShowTest(false);
      onSent?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Test send failed"));
    } finally { setBusy(false); }
  };

  const sendFollowUp = async () => {
    setBusy(true);
    try {
      let id = draftId;
      if (!id) {
        const r = await hqApi.post(`/control/pitches/${commId}/followups`, { subject, html });
        id = r.data.id; setDraftId(id);
      } else {
        await hqApi.patch(`/control/pitches/${commId}/followups/${id}`, { subject, html });
      }
      const r = await hqApi.post(
        `/control/pitches/${commId}/followups/${id}/send`,
        { confirm: true },
      );
      if (r.data.status === "sent") {
        toast.success("Follow-up sent");
        onSent?.();
        onClose?.();
      } else if (r.data.status === "skipped") {
        toast.warning("Send skipped — provider unavailable. The draft is saved.");
      } else {
        toast.error("Send failed. The draft is saved.");
      }
      setShowConfirm(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Send failed"));
    } finally { setBusy(false); }
  };

  const previewSrcDoc = useMemo(() => rendered?.html || "", [rendered]);

  if (!parent) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center p-2 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="followup-generator-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl my-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header ------------------------------------------------------- */}
        <div className="flex items-start justify-between p-5 border-b border-[#EAEAEA]">
          <div>
            <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Follow-Up Generator</div>
            <h2 className="mt-1 font-black text-2xl tracking-[-0.02em]" data-testid="followup-title">
              {asText(parent.business?.name) || "—"}
            </h2>
            <div className="text-[12px] text-[#8A8A8A] mt-0.5">
              {asText(parent.contact?.full_name)} · {asText(parent.contact?.email || parent.pitch?.recipient)}
            </div>
            <div className="text-[11px] text-[#4A4A4A] mt-2">
              <span className="font-bold">Original pitch:</span> {asText(parent.pitch?.subject) || "—"}
              {" · "}
              <span>Sent {fmt(parent.pitch?.sent_at)}</span>
              {" · "}
              <span data-testid="followup-parent-engagement">
                Opens {parent.engagement?.open_count ?? 0} · Clicks {parent.engagement?.click_count ?? 0}
              </span>
            </div>
          </div>
          <button
            data-testid="followup-close"
            onClick={onClose}
            className="p-2 hover:bg-[#F6F5F2] rounded-full"><X size={20}/></button>
        </div>

        {/* Body --------------------------------------------------------- */}
        <div className="p-5 grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* LEFT: subject + editor / preview ------------------------- */}
          <div className="lg:col-span-3 space-y-4">
            <div>
              <label className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Subject</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="An idea for Acme Ltd"
                data-testid="followup-subject-input"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] text-[14px]"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Email design</div>
              <div className="flex gap-2">
                <TabBtn active={tab === "code"}    onClick={() => setTab("code")}   testId="followup-tab-code"><Code2 size={12} className="inline mr-1"/>Code</TabBtn>
                <TabBtn active={tab === "desktop"} onClick={() => { setTab("desktop"); if (!rendered) renderNow({ autoSwitch: false }); }} testId="followup-tab-desktop"><Monitor size={12} className="inline mr-1"/>Desktop</TabBtn>
                <TabBtn active={tab === "mobile"}  onClick={() => { setTab("mobile");  if (!rendered) renderNow({ autoSwitch: false }); }} testId="followup-tab-mobile"><Smartphone size={12} className="inline mr-1"/>Mobile</TabBtn>
              </div>
            </div>

            {tab === "code" && (
              <div className="space-y-2">
                <textarea
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  placeholder={"<html>\n  <body>\n    <p>Hi {{CONTACT_NAME}} — an idea for {{BUSINESS_NAME}}…</p>\n    <a href=\"{{INTEREST_CTA_URL}}\">I'm interested — tell me more</a>\n  </body>\n</html>"}
                  data-testid="followup-html-editor"
                  spellCheck={false}
                  className="w-full font-mono text-[12px] p-3 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] min-h-[420px] leading-relaxed"
                />
                {renderError && (
                  <div className="p-3 rounded-lg bg-[#FDECEC] border border-[#F5B7B1] text-[12px] text-[#8A1E12] flex items-start gap-2"
                       data-testid="followup-render-error">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                    <div>
                      <div className="font-black">Unable to render email</div>
                      <div className="mt-0.5">{renderError}</div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => renderNow()}
                    disabled={busy}
                    data-testid="followup-render-btn"
                    className="px-3 py-2 rounded-full bg-white border border-[#0A0A0A] text-[11px] font-black tracking-[0.16em] uppercase text-[#0A0A0A] disabled:opacity-40"
                  >{busy ? "Rendering…" : "Render HTML"}</button>
                </div>
              </div>
            )}

            {tab !== "code" && (
              <div className="space-y-2">
                {!rendered ? (
                  <Skeleton h={420}/>
                ) : (
                  <div className={`mx-auto ${tab === "desktop" ? "max-w-[720px]" : "max-w-[390px]"}`}>
                    <iframe
                      title="Follow-up preview"
                      srcDoc={previewSrcDoc}
                      sandbox=""
                      data-testid={`followup-preview-${tab}`}
                      className="w-full h-[520px] rounded-xl border border-[#EAEAEA] bg-white"
                    />
                  </div>
                )}
                {rendered?.blocked && (
                  <div className="p-3 rounded-lg bg-[#FEF3E7] border border-[#FCE1C7] text-[12px] text-[#8A2E00] flex items-start gap-2"
                       data-testid="followup-blocked-warning">
                    <AlertTriangle size={14} className="mt-0.5 shrink-0"/>
                    <div>
                      <div className="font-black">Some HTML was blocked for safety</div>
                      <ul className="mt-1 text-[11px] list-disc pl-4 space-y-0.5">
                        {(rendered.warnings || []).slice(0, 8).map((w, i) => (
                          <li key={i} className="font-mono">{w}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {(rendered?.unknown_placeholders || []).length > 0 && (
                  <div className="p-3 rounded-lg bg-[#FFF7EE] border border-[#FCE1C7] text-[12px] text-[#8A2E00]"
                       data-testid="followup-unknown-placeholders">
                    Unknown placeholders present: {" "}
                    {rendered.unknown_placeholders.map((p) => (
                      <code key={p} className="font-mono">{`{{${p}}}`}</code>
                    ))}. They will block sending until removed or corrected.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT: placeholder toolbox + actions ---------------------- */}
          <div className="space-y-4">
            <Card className="p-3">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00] mb-2">Placeholders</div>
              <div className="text-[11px] text-[#8A8A8A] mb-2">
                Only these placeholders will be replaced. Anything else warns before sending.
              </div>
              {APPROVED_PLACEHOLDERS.map((p) => (
                <PlaceholderChip key={p.key} token={p.key} hint={p.hint}
                                 onInsert={insertPlaceholder}/>
              ))}
            </Card>

            <div className="space-y-2">
              <button
                type="button"
                onClick={saveDraft}
                disabled={busy}
                data-testid="followup-save-draft-btn"
                className="w-full px-3 py-2 rounded-full bg-white border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A] hover:border-[#0A0A0A] disabled:opacity-40 inline-flex items-center justify-center gap-2"
              ><Save size={13}/> Save draft</button>
              <button
                type="button"
                onClick={() => setShowTest(true)}
                disabled={busy}
                data-testid="followup-send-test-btn"
                className="w-full px-3 py-2 rounded-full bg-white border border-[#0A0A0A] text-[11px] font-black tracking-[0.16em] uppercase text-[#0A0A0A] disabled:opacity-40 inline-flex items-center justify-center gap-2"
              ><FlaskConical size={13}/> Send test</button>
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={busy || !subject.trim() || !html.trim()}
                data-testid="followup-send-btn"
                className="w-full px-3 py-2 rounded-full bg-[#FF6A00] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40 inline-flex items-center justify-center gap-2"
              ><Send size={13}/> Send follow-up</button>
            </div>
          </div>
        </div>

        {/* Test recipient prompt --------------------------------------- */}
        {showTest && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
               onClick={() => setShowTest(false)}>
            <div className="bg-white rounded-2xl p-5 w-full max-w-md"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="followup-test-prompt">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Send test email</div>
              <p className="text-[12px] text-[#4A4A4A] mt-1">
                Sends a copy to the address below. Subject is prefixed with <span className="font-mono">[TEST]</span>. This never touches the prospect record or engagement metrics.
              </p>
              <label className="text-[10px] tracking-[0.14em] uppercase font-bold text-[#8A8A8A] mt-3 block">Test recipient</label>
              <input
                type="email"
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                data-testid="followup-test-recipient-input"
                className="w-full px-3 py-2 rounded-lg border border-[#EAEAEA] focus:outline-none focus:border-[#FF6A00] text-[13px]"
              />
              <div className="mt-4 flex justify-end gap-2">
                <button onClick={() => setShowTest(false)}
                        className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A]">Cancel</button>
                <button onClick={sendTest} disabled={busy}
                        data-testid="followup-test-confirm-btn"
                        className="px-3 py-2 rounded-full bg-[#0A0A0A] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40">
                  Send test
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Send confirmation ------------------------------------------- */}
        {showConfirm && (
          <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
               onClick={() => setShowConfirm(false)}>
            <div className="bg-white rounded-2xl p-5 w-full max-w-md"
                 onClick={(e) => e.stopPropagation()}
                 data-testid="followup-send-confirm">
              <div className="text-[10px] tracking-[0.24em] uppercase font-black text-[#FF6A00]">Send personalised follow-up?</div>
              <div className="mt-3 text-[13px] space-y-1">
                <div><span className="text-[#8A8A8A] text-[11px] uppercase tracking-[0.14em] font-bold">Business:</span> {asText(parent.business?.name)}</div>
                <div><span className="text-[#8A8A8A] text-[11px] uppercase tracking-[0.14em] font-bold">Contact:</span> {asText(parent.contact?.full_name)}</div>
                <div><span className="text-[#8A8A8A] text-[11px] uppercase tracking-[0.14em] font-bold">Recipient:</span> {asText(parent.contact?.email || parent.pitch?.recipient)}</div>
                <div><span className="text-[#8A8A8A] text-[11px] uppercase tracking-[0.14em] font-bold">Subject:</span> {subject}</div>
              </div>
              <div className="mt-5 flex justify-end gap-2">
                <button onClick={() => setShowConfirm(false)}
                        className="px-3 py-2 rounded-full border border-[#EAEAEA] text-[11px] font-black tracking-[0.16em] uppercase text-[#4A4A4A]">Cancel</button>
                <button onClick={sendFollowUp} disabled={busy}
                        data-testid="followup-send-confirm-btn"
                        className="px-3 py-2 rounded-full bg-[#FF6A00] text-white text-[11px] font-black tracking-[0.16em] uppercase disabled:opacity-40">
                  Send follow-up
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
